/**
 * AlreadySignedInCard — shown on /join/<token> when the visitor is
 * already signed in to a different workspace (or the same one).
 *
 * Surfaces who is signed in + offers a sign-out CTA so the invitee can
 * accept the invite as themselves. We can't silently swap sessions:
 * V1 only allows one membership per user (system-decisions §1), and
 * acceptInvite would throw `AlreadyMemberOfAnotherWorkspaceError`.
 *
 * If the signed-in user already belongs to *this* workspace, we say so
 * and offer a "Go to workspace" shortcut instead — accepting an invite
 * to one's own workspace is a no-op confusion.
 */
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, CardBody } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { meQueryKey } from "../../lib/session.ts";

export interface AlreadySignedInCardProps {
  /** Display name of the currently-signed-in user. */
  currentUserName: string;
  /** The workspace they're currently in. */
  currentWorkspaceName: string;
  /** Workspace this invite is for — used in the prompt copy. */
  inviteWorkspaceName: string;
  /** True when the invite targets the same workspace they're already in. */
  isSameWorkspace: boolean;
}

export function AlreadySignedInCard({
  currentUserName,
  currentWorkspaceName,
  inviteWorkspaceName,
  isSameWorkspace,
}: AlreadySignedInCardProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = trpc.identity.signOut.useMutation({
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: meQueryKey });
      // Stay on the same /join/<token> URL — the page re-renders into
      // the anonymous (phone-OTP) branch on the next pass.
    },
  });

  if (isSameWorkspace) {
    return (
      <Card>
        <CardBody className="flex flex-col gap-3.5">
          <Alert
            intent="ok"
            title="You're already a member"
            description={`This invite is for ${inviteWorkspaceName}, which you already belong to. No further action needed.`}
          />
          <Button type="button" onClick={() => void navigate({ to: "/" })}>
            Go to workspace
          </Button>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardBody className="flex flex-col gap-3.5">
        <Alert
          intent="warn"
          title="Sign out to accept this invite"
          description={
            <>
              You&apos;re currently signed in as <strong>{currentUserName}</strong> to{" "}
              <strong>{currentWorkspaceName}</strong>. Flint keeps one workspace per user, so
              you&apos;ll need to sign out before joining <strong>{inviteWorkspaceName}</strong>.
            </>
          }
        />
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            intent="ghost"
            onClick={() => void navigate({ to: "/" })}
            disabled={signOut.isPending}
          >
            Stay signed in
          </Button>
          <Button type="button" onClick={() => signOut.mutate({})} disabled={signOut.isPending}>
            {signOut.isPending ? "Signing out…" : "Sign out and continue"}
          </Button>
        </div>
        {signOut.error ? <p className="text-xs text-bad">{signOut.error.message}</p> : null}
      </CardBody>
    </Card>
  );
}
